import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { createFragmentContainer, graphql } from 'react-relay';
import get from 'lodash.get';
import { supportedAmpLocales } from 'dibs-intl/exports/constants';
import { ServerVarsContextConsumer } from '../../global/ServerVarsContext/ServerVarsContext';

import { NOT_SUPPORTED_AMP_PAGES } from '../../constants/sbConstants';

class SbSharedHeadComponent extends React.Component {
    getHost() {
        return `https://${this.props.intlVariables.localeDomain}`;
    }

    getHref(host, data) {
        const href = `${host}/${data}`;

        return data.includes('?') ? href : `${href}/`;
    }

    /**
     * hreflangs are added via react-helmet for SB pages because of the need for
     * dynamic hreflangs based on url routing (i.e. we want hreflangs for /furniture,
     * but not for /furniture/?page=2). Otherwise, hreflangs logic (for PDPs and AMP PDPs) is handled
     * via the DBL Server API (in prepareLanguageAnnotations*.js files)
     */
    getHrefLangLinks() {
        const languageAnnotations = get(this.props, 'searchBrowse.languageAnnotations', []);
        return languageAnnotations
            .filter(({ available }) => available)
            .filter(({ href }) => !href.includes('?'))
            .map(({ href, hrefLang }) => ({
                rel: 'alternate',
                href,
                hrefLang,
            }));
    }

    getLinkRel(param) {
        const host = this.getHost();
        const ampHost = `${host}/amp`;
        const canonical = get(this.props, 'searchBrowse.meta.canonical');

        if (canonical) {
            return {
                rel: param,
                href:
                    param === 'amphtml'
                        ? this.getHref(ampHost, canonical)
                        : this.getHref(host, canonical),
            };
        } else {
            return {};
        }
    }

    getPagingMeta(type) {
        const host = this.getHost();
        const data = get(this.props, `searchBrowse.meta.${type}`);

        return data ? { rel: type, href: this.getHref(host, data) } : {};
    }

    getLinks() {
        const { isAmp } = this.props;
        const { locale } = this.props.intlVariables;
        const { meta, pageType } = this.props.searchBrowse;
        const notSupportedAmpPages = pageType && NOT_SUPPORTED_AMP_PAGES.includes(pageType);
        const links = [
            this.getPagingMeta('next'),
            this.getPagingMeta('prev'),
            this.getLinkRel('canonical'),
            ...this.getHrefLangLinks(),
        ];
        if (
            supportedAmpLocales.includes(locale) &&
            !get(meta, 'prev') &&
            !notSupportedAmpPages &&
            !isAmp
        ) {
            links.push(this.getLinkRel('amphtml'));
        }
        return links;
    }

    render() {
        const { searchBrowse } = this.props;
        const metadata = get(searchBrowse, 'meta') || {};
        /**
         * Question - does the tite conflict with the one that is defined in the DBL nunjucks
         * template? It does - https://1stdibs.atlassian.net/browse/FINDING-11968
         */
        const { title = '', description = '', canonical, microdata } = metadata;
        const host = this.getHost();
        const edges = get(searchBrowse, 'edges', []);

        const ogImages = edges.slice(0, 3).map(edge => {
            return {
                name: 'og:image',
                content: get(edge, 'node.photos[0].versions[0].webPath', ''),
            };
        });

        const noFollow = !canonical
            ? {
                  name: 'robots',
                  content: 'noindex, follow',
              }
            : {};

        const ogURL = canonical
            ? {
                  name: 'og:url',
                  content: `${host}/${canonical}/`,
              }
            : {};

        return (
            <ServerVarsContextConsumer>
                {({ clientConf }) => (
                    <React.Fragment>
                        <Helmet
                            title={title}
                            meta={[
                                {
                                    name: 'og:title',
                                    content: title,
                                },
                                {
                                    name: 'og:type',
                                    content: 'website',
                                },
                                {
                                    name: 'description',
                                    content: description,
                                },
                                {
                                    name: 'fb:admins',
                                    content: clientConf.facebookAdmin,
                                },
                                {
                                    name: 'fb:app_id',
                                    content: clientConf.facebookAppId,
                                },
                                {
                                    name: 'fb:page_id',
                                    content: clientConf.facebookPageId,
                                },
                                {
                                    name: 'og:site_name',
                                    content: '1stdibs.com',
                                },
                                {
                                    rel: 'dns-prefetch',
                                    href: process.env.NODE_CDN_HOST,
                                },
                                noFollow,
                                ogURL,
                                ...ogImages,
                            ]}
                            link={this.getLinks()}
                        />
                        <Helmet
                            script={[
                                {
                                    type: 'application/ld+json',
                                    innerHTML: microdata,
                                },
                            ]}
                        />
                    </React.Fragment>
                )}
            </ServerVarsContextConsumer>
        );
    }
}

SbSharedHeadComponent.propTypes = {
    searchBrowse: PropTypes.object.isRequired,
    intlVariables: PropTypes.shape({
        locale: PropTypes.string,
        localeDomain: PropTypes.string,
    }).isRequired,
    isAmp: PropTypes.bool,
};

export const SbSharedHead = createFragmentContainer(
    connect(({ intlVariables }) => ({ intlVariables }))(SbSharedHeadComponent),
    {
        searchBrowse: graphql`
            fragment SbSharedHead_searchBrowse on SearchBrowseQueryConnection {
                meta {
                    title
                    description
                    canonical
                    prev
                    next
                    microdata
                }
                languageAnnotations {
                    href
                    hrefLang
                    available
                }
                pageType
                edges {
                    node {
                        photos(limit: 1) {
                            versions {
                                webPath
                            }
                        }
                    }
                }
            }
        `,
    }
);
